// src/features/organisation/components/employees/EmployeeDetailForm.tsx
import React, { useMemo, useState, useCallback } from 'react';
import { User, UserCheck, Award, Globe, Shield } from 'lucide-react';
import { Employee } from '../../../../types/organisation/employees.types';
import { useEmployee, useEmployeeActions } from '../../hooks/useEmployees';
import { useSites } from '../../hooks/useSites';
import { useDepartments } from '../../hooks/useDepartments';
import { usePositions } from '../../hooks/usePositions';
import { useEmployeeStatuses } from '../../hooks/useReferenceData';
import RoleCompetenceManager from './RoleCompetenceManager';
import MultiSiteManager from './MultiSiteManager';
import {
  EntityFormLayout,
  EntityFormHeader,
  EntityFormFooter,
  EditableField,
} from '../../../../components/common/forms';
import { useEntityForm, FieldConfig, PendingChange } from '../../../../components/common/hooks';
import { apiClient } from '../../../../services/api/client';

interface EmployeeDetailFormProps {
  employeeId: number | null;
  onClose: () => void;
  onSave?: (employee: Employee) => void;
}

type BottomTabType = 'roles' | 'competences' | 'multisite';

const BOTTOM_TABS: { id: BottomTabType; label: string; icon: React.ElementType }[] = [
  { id: 'roles', label: 'Rôles', icon: UserCheck },
  { id: 'competences', label: 'Compétences', icon: Award },
  { id: 'multisite', label: 'Multi-Sites', icon: Globe },
];

const EmployeeDetailForm: React.FC<EmployeeDetailFormProps> = ({
  employeeId,
  onClose,
  onSave: onSaveCallback,
}) => {
  const { employee, loading } = useEmployee(employeeId);
  const { updateEmployee } = useEmployeeActions();
  const { sites } = useSites();
  const { departments } = useDepartments();
  const { positions } = usePositions();
  const { employeeStatuses } = useEmployeeStatuses();

  const [activeBottomTab, setActiveBottomTab] = useState<BottomTabType>('roles');
  const [pendingRoleChanges, setPendingRoleChanges] = useState<PendingChange[]>([]);
  const [pendingCompetenceChanges, setPendingCompetenceChanges] = useState<PendingChange[]>([]);
  const [pendingMultiSiteChanges, setPendingMultiSiteChanges] = useState<PendingChange[]>([]);

  const fields: FieldConfig[] = useMemo(() => [
    { name: 'employeeCode', label: 'Code employé', type: 'text', required: true, disabled: !!employeeId },
    { name: 'firstName', label: 'Prénom', type: 'text', required: true },
    { name: 'lastName', label: 'Nom de famille', type: 'text', required: true },
    { name: 'email', label: 'Email', type: 'email', required: true },
    { name: 'phone', label: 'Téléphone fixe', type: 'tel' },
    { name: 'mobile', label: 'Téléphone mobile', type: 'tel' },
    {
      name: 'siteId',
      label: 'Site',
      type: 'select',
      required: true,
      options: sites.map(s => ({ value: s.id, label: s.siteName })),
      render: (value) => sites.find(s => s.id === value)?.siteName || 'Non défini',
    },
    {
      name: 'departmentId',
      label: 'Département',
      type: 'select',
      options: departments.map(d => ({ value: d.id, label: d.departmentName })),
      render: (value) => departments.find(d => d.id === value)?.departmentName || 'Non défini',
    },
    {
      name: 'positionId',
      label: 'Poste',
      type: 'select',
      options: positions.map(p => ({ value: p.id, label: p.positionName })),
      render: (value) => positions.find(p => p.id === value)?.positionName || 'Non défini',
    },
    { name: 'hireDate', label: "Date d'embauche", type: 'date' },
    { name: 'birthDate', label: 'Date de naissance', type: 'date' },
    {
      name: 'statusCode',
      label: 'Statut',
      type: 'select',
      options: employeeStatuses.map(s => ({ value: s.code, label: s.name })),
      render: (value) => employeeStatuses.find(s => s.code === value)?.name || value,
    },
    { name: 'isQhseResponsible', label: 'Responsable QHSE', type: 'checkbox' },
    { name: 'notes', label: 'Notes', type: 'textarea', colSpan: 2 },
  ], [sites, departments, positions, employeeStatuses, employeeId]);

  const transformToFormData = useCallback((emp: Employee | null) => {
    if (!emp) return null;
    return {
      employeeCode: emp.employeeCode || '',
      firstName: emp.firstName || '',
      lastName: emp.lastName || '',
      email: emp.email || '',
      phone: emp.phone || '',
      mobile: emp.mobile || '',
      siteId: emp.site?.id || 0,
      departmentId: emp.department?.id || 0,
      positionId: emp.position?.id || 0,
      hireDate: emp.hireDate || '',
      birthDate: emp.birthDate || '',
      statusCode: emp.employeeStatus?.code || '',
      isQhseResponsible: emp.isQhseResponsible || false,
      notes: emp.notes || '',
    };
  }, []);

  const handleSave = async (formData: Record<string, unknown>) => {
    if (!employeeId) return;

    await updateEmployee(employeeId, formData);

    for (const change of pendingRoleChanges) {
      if (change.type === 'add' && change.data) {
        await apiClient.post('/organisation/role-assignments', {
          employeeId,
          roleId: change.data.roleId,
          siteId: change.data.siteId || null,
          assignedById: null,
        });
      } else if (change.type === 'remove' && change.id) {
        await apiClient.delete(`/organisation/role-assignments/${change.id}`);
      } else if (change.type === 'modify' && change.id && change.data) {
        await apiClient.put(`/organisation/role-assignments/${change.id}`, {
          roleId: change.data.roleId,
          siteId: change.data.siteId || null,
        });
      }
    }

    for (const change of pendingCompetenceChanges) {
      if (change.type === 'add' && change.data) {
        await apiClient.post(`/organisation/competences/employee/${employeeId}`, {
          competenceName: change.data.competenceName,
          competenceLevel: change.data.competenceLevel || null,
          certificationDate: change.data.certificationDate || null,
          expiryDate: change.data.expiryDate || null,
          certifyingBody: change.data.certifyingBody || null,
        });
      } else if (change.type === 'remove' && change.id) {
        await apiClient.delete(`/organisation/competences/${change.id}`);
      } else if (change.type === 'modify' && change.id && change.data) {
        await apiClient.put(`/organisation/competences/${change.id}`, {
          competenceName: change.data.competenceName,
          competenceLevel: change.data.competenceLevel || null,
          certificationDate: change.data.certificationDate || null,
          expiryDate: change.data.expiryDate || null,
          certifyingBody: change.data.certifyingBody || null,
        });
      }
    }

    for (const change of pendingMultiSiteChanges) {
      if (change.type === 'add' && change.data) {
        await apiClient.post(`/organisation/employees/${employeeId}/multi-site-assignments`, {
          siteId: change.data.siteId,
          role: change.data.role,
          assignmentType: change.data.assignmentType,
          effectiveFrom: change.data.effectiveFrom,
          effectiveTo: change.data.effectiveTo || null,
        });
      } else if (change.type === 'remove' && change.id) {
        await apiClient.delete(`/organisation/employees/${employeeId}/multi-site-assignments/${change.id}`);
      } else if (change.type === 'modify' && change.id && change.data) {
        await apiClient.put(`/organisation/employees/${employeeId}/multi-site-assignments/${change.id}`, {
          siteId: change.data.siteId,
          role: change.data.role,
          assignmentType: change.data.assignmentType,
          effectiveFrom: change.data.effectiveFrom,
          effectiveTo: change.data.effectiveTo || null,
        });
      }
    }

    setPendingRoleChanges([]);
    setPendingCompetenceChanges([]);
    setPendingMultiSiteChanges([]);

    const finalEmployee = await apiClient.get(`/organisation/employees/${employeeId}`);
    onSaveCallback?.(finalEmployee);
    onClose();
    return finalEmployee;
  };

  const {
    formData,
    hasChanges: formHasChanges,
    saving,
    errors,
    handleFieldChange,
    startEditing,
    stopEditing,
    isEditing,
    handleSave: onFormSave,
  } = useEntityForm({
    entity: employee,
    fields,
    onSave: handleSave,
    transformToFormData,
  });

  const hasChanges = formHasChanges ||
    pendingRoleChanges.length > 0 ||
    pendingCompetenceChanges.length > 0 ||
    pendingMultiSiteChanges.length > 0;

  const totalPendingChanges = pendingRoleChanges.length + pendingCompetenceChanges.length + pendingMultiSiteChanges.length;

  const handleRoleChange = (change: Omit<PendingChange, 'section'>) => {
    setPendingRoleChanges(prev => [...prev, { ...change, section: 'roles' }]);
  };

  const handleCompetenceChange = (change: Omit<PendingChange, 'section'>) => {
    setPendingCompetenceChanges(prev => [...prev, { ...change, section: 'competences' }]);
  };

  const handleMultiSiteChange = (change: Omit<PendingChange, 'section'>) => {
    setPendingMultiSiteChanges(prev => [...prev, { ...change, section: 'multisite' }]);
  };

  if (!formData && !loading) return null;

  const tabCounts: Record<BottomTabType, number> = {
    roles: employee?.roleAssignments?.length || 0,
    competences: employee?.competences?.length || 0,
    multisite: employee?.multiSiteAssignments?.length || 0,
  };

  return (
    <EntityFormLayout loading={loading} maxWidth="6xl">
      <EntityFormHeader
        title={employee?.fullName || 'Employé'}
        subtitle={employee ? `Code: ${employee.employeeCode}` : undefined}
        icon={User}
        hasChanges={hasChanges}
        saving={saving}
        onSave={onFormSave}
        onClose={onClose}
        badges={[{ show: (emp) => emp?.isQhseResponsible, label: 'QHSE', icon: Shield }]}
        entity={employee}
      />

      <div className="flex-1 overflow-y-auto p-6">
        {/* Section 1: Informations (toujours visible) */}
        {formData && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations</h3>
            <div className="grid grid-cols-2 gap-4">
              {fields.map(field => (
                <div key={field.name} className={field.colSpan === 2 ? 'col-span-2' : ''}>
                  <EditableField
                    field={field}
                    value={formData[field.name]}
                    isEditing={isEditing(field.name)}
                    onStartEdit={() => startEditing(field.name)}
                    onStopEdit={() => stopEditing(field.name)}
                    onChange={(value) => handleFieldChange(field.name, value)}
                    error={errors[field.name]}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Section 2: Onglets du bas (Rôles, Compétences, Multi-Sites) */}
        {employee && (
          <div className="mt-8">
            <div className="border-b">
              <nav className="flex space-x-4">
                {BOTTOM_TABS.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveBottomTab(tab.id)}
                    className={`flex items-center gap-2 py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                      activeBottomTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <tab.icon className="h-4 w-4" />
                    {tab.label}
                    {tabCounts[tab.id] > 0 && (
                      <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                        activeBottomTab === tab.id
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {tabCounts[tab.id]}
                      </span>
                    )}
                  </button>
                ))}
              </nav>
            </div>

            <div className="mt-6">
              {activeBottomTab === 'roles' && (
                <RoleCompetenceManager
                  employee={employee}
                  showOnly="roles"
                  onRoleChange={handleRoleChange}
                />
              )}
              {activeBottomTab === 'competences' && (
                <RoleCompetenceManager
                  employee={employee}
                  showOnly="competences"
                  onCompetenceChange={handleCompetenceChange}
                />
              )}
              {activeBottomTab === 'multisite' && (
                <MultiSiteManager
                  employee={employee}
                  onMultiSiteChange={handleMultiSiteChange}
                />
              )}
            </div>
          </div>
        )}
      </div>

      <EntityFormFooter
        helpText="💡 Double-cliquez sur un champ pour le modifier."
        pendingChangesCount={totalPendingChanges}
      />
    </EntityFormLayout>
  );
};

export default EmployeeDetailForm;
